import React, { ChangeEvent, FocusEvent, useState } from 'react'
import { MdInfoOutline } from 'react-icons/md'

type TInput = {
  readonly id?: string
  readonly name?: string
  readonly type: 'text' | 'tel' | 'number' | 'password' | 'date' | 'textarea'
  readonly caption?: string
  readonly label: string
  readonly value?: string | number
  readonly onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  readonly onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  readonly error?: string
  readonly tooltip?: string
  readonly maxLength?: number
}

export default function Input({
  id,
  name,
  type,
  caption,
  label,
  value,
  onChange,
  onBlur,
  error,
  tooltip,
  maxLength,
}: TInput) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div className='flex w-full flex-col gap-2'>
      <div className='flex flex-row items-center gap-2'>
        <span className='text-base font-medium text-pop-gray-1'>{caption}</span>
        {tooltip && (
          <div className='relative inline-block'>
            <MdInfoOutline
              className='h-5 w-5 text-pop-grey-600'
              onClick={() => setIsHovered(true)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            {isHovered && (
              <div className='absolute -left-32 top-9 z-[999] w-80 -translate-y-2 transform rounded-md bg-pop-neutral p-3 text-white opacity-100 transition-opacity duration-300'>
                <div className='text-xs font-normal text-white'>{tooltip}</div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={`w-full rounded-lg border ${
          error ? 'border-pop-error' : 'border-pop-gray-5'
        } p-1`}
      >
        {type === 'date' && (
          <input
            id={id}
            name={name}
            type='date'
            placeholder={label}
            value={value as string}
            onChange={onChange}
            onBlur={onBlur}
            className='sentry-mask w-full p-2 font-light placeholder:font-light placeholder:text-pop-neutral-gray focus:outline-none'
          />
        )}

        {type === 'textarea' && (
          <textarea
            id={id}
            name={name}
            placeholder={label}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className='no-spinner w-full p-2 font-light placeholder:font-light placeholder:text-pop-neutral-gray focus:outline-none'
            {...(maxLength && {
              maxLength: maxLength,
            })}
          />
        )}

        {['text', 'tel', 'number', 'password'].includes(type) && (
          <input
            id={id}
            name={name}
            type={type}
            placeholder={label}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            {...(type === 'number' && {
              onWheel: (e) => e.currentTarget.blur(),
              pattern: '[0-9]*',
              inputMode: 'numeric',
            })}
            {...(maxLength && {
              maxLength: maxLength,
            })}
            className='no-spinner w-full p-2 font-light placeholder:font-light placeholder:text-pop-neutral-gray focus:outline-none'
          />
        )}
      </div>
      {!!error && <span className='w-full text-left text-xs text-pop-error'>{error}</span>}
    </div>
  )
}
